import React from 'react';
import { Link } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { FiShoppingBag, FiStar } from 'react-icons/fi';
import { toast } from 'react-toastify';
import './ProductCard.css';

// Base URL for serving static files (images)
const getBaseUrl = () => {
  // In production (monolithic deployment), images are served from same domain
  if (process.env.NODE_ENV === 'production') {
    return '';
  }
  // In development, get base URL from API URL
  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
  return apiUrl.replace(/\/api\/?$/, '');
};

// Generate consistent pseudo-random rating based on product ID
const generateRating = (productId) => {
  const hash = productId.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  const rating = 3.9 + (hash % 10) * 0.1;
  const reviews = 30 + (hash % 71);
  return { rating: Math.min(parseFloat(rating.toFixed(1)), 4.8), reviews };
};

// Check for special badges based on product name
const getSpecialBadge = (productName) => {
  const name = productName?.toLowerCase() || '';
  if (name.includes('drools') && name.includes('chicken') && name.includes('egg')) {
    return { type: 'most-ordered', label: 'Most Ordered' };
  }
  if (name.includes('chappi') && name.includes('chicken & rice')) {
    return { type: 'bestseller', label: 'Bestseller' };
  }
  return null;
};

const ProductCard = ({ product }) => {
  const { addToCart } = useCart();
  
  // Generate rating and check for special badge
  const { rating, reviews } = generateRating(product._id);
  const specialBadge = getSpecialBadge(product.name);

  const handleAddToCart = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Check if product has variants
    if (product.hasVariants && product.variants?.length > 0) {
      // Find first available variant
      const availableVariant = product.variants.find(v => v.stock > 0);
      if (availableVariant) {
        const cartItem = {
          ...product,
          price: availableVariant.price,
          weight: availableVariant.weight,
          stock: availableVariant.stock,
          variantId: availableVariant._id,
          cartItemId: `${product._id}-${availableVariant._id}`
        };
        addToCart(cartItem);
        // item added
      } else {
        toast.error('Product is out of stock');
      }
    } else {
      if (product.stock > 0) {
        addToCart(product);
        // item added
      } else {
        toast.error('Product is out of stock');
      }
    }
  };

  // Handle image URL - support both uploaded images and external URLs
  const getImageUrl = (imageUrl) => {
    if (!imageUrl) return 'https://via.placeholder.com/400x400?text=No+Image';
    if (imageUrl.startsWith('/uploads/')) {
      return `${getBaseUrl()}${imageUrl}`;
    }
    return imageUrl;
  };

  // Get the primary image (from images array or fallback to imageUrl)
  const getPrimaryImage = () => {
    // Use primaryImage virtual if available
    if (product.primaryImage) {
      return product.primaryImage;
    }
    // Fallback to first image in array
    if (product.images && product.images.length > 0) {
      const primary = product.images.find(img => img.isPrimary);
      return primary ? primary.url : product.images[0].url;
    }
    // Fallback to imageUrl
    return product.imageUrl;
  };

  const formatCategory = (category) => {
    return category?.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || '';
  };

  // Get display price (minimum price for products with variants)
  const getDisplayPrice = () => {
    if (product.hasVariants && product.variants?.length > 0) {
      const minPrice = Math.min(...product.variants.map(v => v.price));
      return minPrice;
    }
    return product.price;
  };

  // Get total stock for products with variants
  const getTotalStock = () => {
    if (product.hasVariants && product.variants?.length > 0) {
      return product.variants.reduce((sum, v) => sum + v.stock, 0);
    }
    return product.stock;
  };

  // Check if product has multiple sizes
  const hasMultipleSizes = product.hasVariants && product.variants?.length > 1;

  return (
    <Link to={`/product/${product._id}`} className="product-card">
      <div className="product-image-wrapper">
        <img 
          src={getImageUrl(getPrimaryImage())} 
          alt={product.name}
          className="product-image"
          loading="lazy"
        />
        {/* Special badges take priority */}
        {specialBadge && (
          <span className={`badge badge-${specialBadge.type}`}>{specialBadge.label}</span>
        )}
        {/* Stock badges - only show if no special badge */}
        {!specialBadge && getTotalStock() <= 5 && getTotalStock() > 0 && (
          <span className="badge badge-warning">Low Stock</span>
        )}
        {getTotalStock() === 0 && (
          <span className="badge badge-danger">Out of Stock</span>
        )}
        {!specialBadge && product.featured && (
          <span className="badge badge-featured">Featured</span>
        )}
        <button 
          className="quick-add-btn"
          onClick={handleAddToCart}
          disabled={getTotalStock() === 0}
          aria-label="Add to cart"
        >
          <FiShoppingBag />
        </button>
      </div>

      <div className="product-details">
        <div className="product-meta">
          <span className="product-category">{formatCategory(product.category)}</span>
          <div className="product-rating">
            <FiStar className="star-filled" />
            <span>{rating.toFixed(1)}</span>
            <span className="review-count">({reviews})</span>
          </div>
        </div>
        
        <h3 className="product-name">{product.name}</h3>
        
        <div className="product-footer">
          <span className="product-price">
            {hasMultipleSizes && <span className="price-from">From </span>}
            ₹{getDisplayPrice().toLocaleString()}
          </span>
          <span className="product-weight">
            {hasMultipleSizes ? `${product.variants.length} sizes` : product.weight}
          </span>
        </div>
      </div>
    </Link>
  );
};

export default ProductCard;
